import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { XIcon } from "lucide-react";
import type { SavingsEvent } from "@/api/hooks/use-billing-statements";
import {
  useBillingContact,
  useFileDispute,
} from "@/api/hooks/use-billing-disputes";

interface DisputeModalProps {
  open: boolean;
  onClose: () => void;
  periodId: string;
  tenantId: string;
  events: SavingsEvent[];
  onFiled: () => void;
}

export function DisputeModal({
  open,
  onClose,
  periodId,
  tenantId,
  events,
  onFiled,
}: DisputeModalProps) {
  const contactQ = useBillingContact(tenantId);
  const file = useFileDispute(periodId);

  const [reason, setReason] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [formError, setFormError] = useState<string | null>(null);

  // Pre-fill contact_email once the contact loads.
  useEffect(() => {
    if (contactQ.data?.billing_email && !contactEmail) {
      setContactEmail(contactQ.data.billing_email);
    }
  }, [contactQ.data, contactEmail]);

  useEffect(() => {
    if (open) return;
    setReason("");
    setSelectedIds(new Set());
    setFormError(null);
    file.reset();
    // file is a useMutation result; its identity may change between
    // renders even when nothing meaningful changed. Including it in deps
    // can cause this effect to re-run on every render, which sets
    // selectedIds to a new Set() each time → infinite render loop
    // (React error #185). The intent here is "reset on close" — `open`
    // is the only correct dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", onKey);
      return () => {
        document.body.style.overflow = prev;
        document.removeEventListener("keydown", onKey);
      };
    }
  }, [open, onClose]);

  if (!open) return null;

  function toggleEvent(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setFormError("Please describe the reason for the dispute.");
      return;
    }
    const trimmedEmail = contactEmail.trim();
    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      setFormError("A valid contact email is required.");
      return;
    }

    try {
      await file.mutateAsync({
        reason: trimmedReason,
        contact_email: trimmedEmail,
        disputed_event_ids: selectedIds.size > 0 ? Array.from(selectedIds) : undefined,
      });
      onFiled();
      onClose();
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to file dispute.";
      setFormError(msg);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="File a dispute"
    >
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg border border-line bg-surface shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line-subtle bg-surface px-5 py-3">
          <h2 className="text-sm font-semibold text-fg">
            File a dispute
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1.5 text-fg-muted hover:text-fg hover:bg-surface-3 focus:outline-none focus:ring-1 focus:ring-electric-blue transition-colors"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <p className="text-xs text-fg-muted">
            You can dispute a statement until it is invoiced, and within 15 days of it being
            finalized. While a dispute is open, the statement is not invoiced or charged. Phantm
            resolves disputes within 30 days.
          </p>

          <label className="block">
            <span className="text-xs font-medium text-fg">
              Reason <span className="text-red-400">*</span>
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={5}
              required
              maxLength={10_000}
              placeholder="Describe what is being disputed and why…"
              className="mt-1 w-full rounded border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-faint focus:outline-none focus:ring-1 focus:ring-electric-blue"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-fg">
              Contact email <span className="text-red-400">*</span>
            </span>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              required
              maxLength={255}
              className="mt-1 w-full rounded border border-line bg-canvas px-3 py-2 text-sm text-fg focus:outline-none focus:ring-1 focus:ring-electric-blue"
            />
          </label>

          <div>
            <p className="text-xs font-medium text-fg">
              Specific events contested (optional)
            </p>
            <p className="text-[11px] text-fg-muted mt-0.5">
              Leave blank to dispute the entire period.
            </p>
            <div className="mt-2 max-h-48 overflow-y-auto rounded border border-line-subtle bg-canvas">
              {events.length === 0 ? (
                <p className="px-3 py-2 text-xs text-fg-muted">
                  No events loaded on this page.
                </p>
              ) : (
                <ul className="divide-y divide-line-subtle">
                  {events.map((ev) => (
                    <li
                      key={ev.id}
                      className="flex items-center gap-2 px-3 py-1.5 text-xs"
                    >
                      <input
                        type="checkbox"
                        id={`ev-${ev.id}`}
                        checked={selectedIds.has(ev.id)}
                        onChange={() => toggleEvent(ev.id)}
                        className="accent-electric-blue"
                      />
                      <label
                        htmlFor={`ev-${ev.id}`}
                        className="flex-1 cursor-pointer truncate text-fg-muted"
                      >
                        <span className="font-mono text-fg/80">
                          {ev.id.slice(0, 8)}
                        </span>{" "}
                        · {ev.baseline_model} → {ev.actual_model}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {selectedIds.size > 0 && (
              <p className="mt-1 text-[11px] text-fg-muted">
                {selectedIds.size} event{selectedIds.size === 1 ? "" : "s"} selected
              </p>
            )}
          </div>

          {formError && (
            <p className="text-xs text-red-400" role="alert">
              {formError}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 border-t border-line-subtle pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-line px-3 py-1.5 text-xs text-fg-muted hover:bg-surface-2 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={file.isPending}
              className="rounded border border-electric-blue bg-electric-blue/20 px-3 py-1.5 text-xs font-medium text-fg hover:bg-electric-blue/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {file.isPending ? "Filing…" : "File dispute"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
